/* eslint-disable no-console */
import {
	PAID_COLUMN_MAX_WIDTH,
	PAID_COLUMN_MIN_WIDTH,
	REGISTRATION_COLUMN_MAX_WIDTH,
	REGISTRATION_COLUMN_MIN_WIDTH,
} from './constants';
import { ResponsesData, TableBodyProps } from 'features/CustomFieldsResponses/types';
import { downloadTicketQR, formatDate, formatStatusLabel } from './utils';

import { AttendeeActions } from '../AttendeeActions';
import { DataRow, StickyActionCell } from './PunchupResponsesTable.styles';
import { FC } from 'react';
import { Table } from 'features/common/components/Table';
import { useTranslation } from 'react-i18next';

export const PunchupTableBody: FC<TableBodyProps> = ({
	responsesQueryObject,
	questions,
	isAttendeesList,
	showDownloadQR = false,
	columnsN = 0,
	gridColumns = `repeat(${columnsN + 1}, 1fr)`,
	eventMode,
	actionWidth = 40,
}) => {
	const { t } = useTranslation();
	const { data: responses } = responsesQueryObject;

	const renderCell = (value: string | number, minWidth: number, maxWidth?: number, key?: string) => (
		<Table.Cell minWidth={minWidth} maxWidth={maxWidth} key={key}>
			{value}
		</Table.Cell>
	);

	const renderDownloadQRCell = (data: ResponsesData, minWidth: number, maxWidth?: number) => (
		<Table.Cell minWidth={minWidth} maxWidth={maxWidth} textAlign="center">
			{data.can_download_qr ? (
				<Table.Button
					onClick={() => downloadTicketQR(data.ticket_id, data.email)}
					text={t('download')}
					category="outlined"
					className="small"
				/>
			) : (
				'-'
			)}
		</Table.Cell>
	);

	const renderScannedCell = (data: ResponsesData, minWidth: number, maxWidth?: number) => (
		<Table.Cell minWidth={minWidth} maxWidth={maxWidth} textAlign="center">
			{data.is_validated ? (
				<span style={{ color: '#10b981', fontWeight: 600 }}>
					✓ Scanned {data.scan_count && data.scan_count > 1 ? `(${data.scan_count}x)` : ''}
				</span>
			) : (
				<span style={{ color: '#6b7280' }}>Not Scanned</span>
			)}
		</Table.Cell>
	);

	const renderPromotionCell = (data: ResponsesData, minWidth: number, maxWidth?: number) => (
		<Table.Cell minWidth={minWidth} maxWidth={maxWidth} textAlign="center">
			{data.promotion_used ? (
				<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
					<span style={{ color: '#8b5cf6', fontWeight: 600 }}>{data.promotion_used.code}</span>
					<span style={{ fontSize: '12px', color: '#6b7280' }}>-${data.promotion_used.amount.toFixed(2)}</span>
				</div>
			) : (
				'-'
			)}
		</Table.Cell>
	);

	const renderGiftCardsCell = (data: ResponsesData, minWidth: number, maxWidth?: number) => (
		<Table.Cell minWidth={minWidth} maxWidth={maxWidth} textAlign="center">
			{data.gift_cards_used && data.gift_cards_used.length > 0 ? (
				<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
					{data.gift_cards_used.map((gc, idx) => (
						<div key={idx} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
							<span style={{ color: '#f59e0b', fontWeight: 600, fontSize: '12px' }}>****{gc.last4}</span>
							<span style={{ fontSize: '11px', color: '#6b7280' }}>-${gc.applied_amount.toFixed(2)}</span>
						</div>
					))}
				</div>
			) : (
				'-'
			)}
		</Table.Cell>
	);

	const renderAddonsCell = (data: ResponsesData, minWidth: number, maxWidth?: number) => {
		if (!data.addons_used || data.addons_used.length === 0) {
			return (
				<Table.Cell minWidth={minWidth} maxWidth={maxWidth} textAlign="center">
					-
				</Table.Cell>
			);
		}

		const addonNames = data.addons_used.map((addon) => addon.name).join(', ');
		const totalPrice = data.addons_used.reduce((sum, addon) => sum + addon.price, 0);

		return (
			<Table.Cell minWidth={minWidth} maxWidth={maxWidth} textAlign="center">
				<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
					<span style={{ color: '#06b6d4', fontWeight: 600, fontSize: '12px' }}>{addonNames}</span>
					<span style={{ fontSize: '11px', color: '#6b7280' }}>${totalPrice.toFixed(2)}</span>
				</div>
			</Table.Cell>
		);
	};
	const renderSeatCell = (seatId: string, minWidth: number, maxWidth?: number, key?: string) => (
		<Table.Cell minWidth={minWidth} maxWidth={maxWidth} key={key}>
			<div
				style={{
					wordWrap: 'break-word',
					whiteSpace: 'normal',
					lineHeight: '1.4',
					maxHeight: '2.8em',
					overflow: 'hidden',
					textOverflow: 'ellipsis',
					display: '-webkit-box',
					WebkitLineClamp: 2,
					WebkitBoxOrient: 'vertical',
				}}
			>
				{seatId}
			</div>
		</Table.Cell>
	);

	if (!responses) {
		return null;
	}

	const usePaidAttendeesLayout = isAttendeesList && eventMode === 'normal';

	// Width arrays for the legacy code path (registration mode, and the
	// responses view that reverses the trailing columns). Positional with the
	// REGISTRATION_COLUMN_HEADINGS order.
	const COLUMN_MIN_WIDTH = REGISTRATION_COLUMN_MIN_WIDTH;
	const COLUMN_MAX_WIDTH = REGISTRATION_COLUMN_MAX_WIDTH;

	return (
		<tbody>
			{responses.pages.map((page: ResponsesData[]) => {
				const transactionTickets: Record<string, number[]> = {};
				for (const r of page) {
					if (!transactionTickets[r.order_id]) {
						transactionTickets[r.order_id] = [];
					}

					transactionTickets[r.order_id].push(r.ticket_id);
				}
				return page.map((data: ResponsesData) => {
					const filteredResponses = data.responses.filter(
						(response) => !['ATTENDEE_NAME', 'ATTENDEE_EMAIL'].includes(response.custom_field.name)
					);

					// Extract seat_id from data directly or from tickets array
					const seatId =
						data.seat_id ||
						data.tickets
							?.map((ticket) => ticket.seat_id)
							.filter(Boolean)
							?.join(', ') ||
						'-';

					if (usePaidAttendeesLayout) {
						// Paid + attendee-list column order:
						// fullName(0), seat(1), email(2), phone(3), date(4), amount(5),
						// orderId(6), type(7), status(8), ticketTypes(9),
						// scanned(10), promotion(11), giftCards(12), addons(13)
						const RESPONSE_CELL_MIN_WIDTH = 250;
						const RESPONSE_CELL_MAX_WIDTH = 450;
						const responseCells = questions.map((question) => {
							const response = filteredResponses.find((r) => r.custom_field.name === question);
							let value = response?.string_value || '-';
							if (response && response.choice_id > 0) {
								value = response.choice.name;
							}
							return renderCell(value, RESPONSE_CELL_MIN_WIDTH, RESPONSE_CELL_MAX_WIDTH, question);
						});

						return (
							<DataRow gridColumns={gridColumns} key={`${data.order_id}_${data.ticket_id}`} textAlign="center">
								{renderCell(data.full_name || '-', PAID_COLUMN_MIN_WIDTH[0], PAID_COLUMN_MAX_WIDTH[0], 'full_name')}
								{renderSeatCell(seatId, PAID_COLUMN_MIN_WIDTH[1], PAID_COLUMN_MAX_WIDTH[1], 'seat_id')}
								{renderCell(data.email || '-', PAID_COLUMN_MIN_WIDTH[2], PAID_COLUMN_MAX_WIDTH[2], 'email')}
								{renderCell(data.phone_number || '-', PAID_COLUMN_MIN_WIDTH[3], PAID_COLUMN_MAX_WIDTH[3], 'phone')}
								{renderCell(formatDate(data.date), PAID_COLUMN_MIN_WIDTH[4], PAID_COLUMN_MAX_WIDTH[4], 'date')}
								{renderCell(data.amount.toFixed(2), PAID_COLUMN_MIN_WIDTH[5], PAID_COLUMN_MAX_WIDTH[5], 'amount')}
								{renderCell(data.order_id, PAID_COLUMN_MIN_WIDTH[6], PAID_COLUMN_MAX_WIDTH[6], 'order_id')}
								{renderCell(
									data.is_buyer ? 'Buyer' : 'Attendee',
									PAID_COLUMN_MIN_WIDTH[7],
									PAID_COLUMN_MAX_WIDTH[7],
									'type'
								)}
								{renderCell(
									formatStatusLabel(data.status),
									PAID_COLUMN_MIN_WIDTH[8],
									PAID_COLUMN_MAX_WIDTH[8],
									'status'
								)}
								{renderCell(
									data.ticket_types_string,
									PAID_COLUMN_MIN_WIDTH[9],
									PAID_COLUMN_MAX_WIDTH[9],
									'ticket_types_string'
								)}
								{renderScannedCell(data, PAID_COLUMN_MIN_WIDTH[10], PAID_COLUMN_MAX_WIDTH[10])}
								{renderPromotionCell(data, PAID_COLUMN_MIN_WIDTH[11], PAID_COLUMN_MAX_WIDTH[11])}
								{renderGiftCardsCell(data, PAID_COLUMN_MIN_WIDTH[12], PAID_COLUMN_MAX_WIDTH[12])}
								{renderAddonsCell(data, PAID_COLUMN_MIN_WIDTH[13], PAID_COLUMN_MAX_WIDTH[13])}

								{responseCells}
								{showDownloadQR && renderDownloadQRCell(data, PAID_COLUMN_MIN_WIDTH[15])}

								<StickyActionCell minWidth={actionWidth} maxWidth={actionWidth} textAlign="center" key="actions">
									<AttendeeActions data={data} transactionTickets={transactionTickets} />
								</StickyActionCell>
							</DataRow>
						);
					}

					let cols = [];

					if (eventMode === 'normal') {
						cols = [
							renderCell(data.amount.toFixed(2), COLUMN_MIN_WIDTH[5], undefined, 'amount'),
							renderCell(formatDate(data.date), COLUMN_MIN_WIDTH[6], undefined, 'date'),
							renderCell(formatStatusLabel(data.status), COLUMN_MIN_WIDTH[7], undefined, 'status'),
							renderCell(data.ticket_types_string, COLUMN_MIN_WIDTH[8], undefined, 'ticket_types_string'),
							renderScannedCell(data, COLUMN_MIN_WIDTH[9], COLUMN_MAX_WIDTH[9]),
							renderPromotionCell(data, COLUMN_MIN_WIDTH[10], COLUMN_MAX_WIDTH[10]),
							renderGiftCardsCell(data, COLUMN_MIN_WIDTH[11], COLUMN_MAX_WIDTH[11]),
							renderAddonsCell(data, COLUMN_MIN_WIDTH[12], COLUMN_MAX_WIDTH[12]),
							renderSeatCell(seatId, COLUMN_MIN_WIDTH[12], COLUMN_MAX_WIDTH[12], 'seat_id'),
						];
					} else {
						cols = [
							renderCell(formatDate(data.date), COLUMN_MIN_WIDTH[6], undefined, 'date'),
							renderCell(data.ticket_types_string, COLUMN_MIN_WIDTH[8], undefined, 'ticket_types_string'),
							renderScannedCell(data, COLUMN_MIN_WIDTH[9], COLUMN_MAX_WIDTH[9]),
							renderPromotionCell(data, COLUMN_MIN_WIDTH[10], COLUMN_MAX_WIDTH[10]),
							renderGiftCardsCell(data, COLUMN_MIN_WIDTH[11], COLUMN_MAX_WIDTH[11]),
							renderAddonsCell(data, COLUMN_MIN_WIDTH[12], COLUMN_MAX_WIDTH[12]),
							renderSeatCell(seatId, COLUMN_MIN_WIDTH[12], COLUMN_MAX_WIDTH[12], 'seat_id'),
						];
					}

					cols = cols.concat(
						questions.map((question) => {
							const response = filteredResponses.find((r) => r.custom_field.name === question);
							let value = response?.string_value || '-';
							if (response && response.choice_id > 0) {
								value = response.choice.name;
							}
							return renderCell(value, COLUMN_MIN_WIDTH[4], COLUMN_MAX_WIDTH[4], question);
						})
					);

					return (
						<DataRow gridColumns={gridColumns} key={`${data.order_id}_${data.ticket_id}`} textAlign="center">
							{renderCell(data.order_id, COLUMN_MIN_WIDTH[0], undefined, 'order_id')}
							{renderCell(data.is_buyer ? 'Buyer' : 'Attendee', COLUMN_MIN_WIDTH[1], undefined, 'type')}
							{renderCell(data.email || '-', COLUMN_MIN_WIDTH[2], undefined, 'email')}
							{renderCell(data.full_name || '-', COLUMN_MIN_WIDTH[3], undefined, 'full_name')}
							{renderCell(data.phone_number || '-', COLUMN_MIN_WIDTH[4], undefined, 'phone')}

							{isAttendeesList ? cols : [...cols].reverse()}
							{isAttendeesList && showDownloadQR && renderDownloadQRCell(data, COLUMN_MIN_WIDTH[9])}

							{/* Columna de acciones */}
							<StickyActionCell minWidth={80} maxWidth={80} textAlign="center" key="actions">
								<AttendeeActions data={data} transactionTickets={transactionTickets} />
							</StickyActionCell>
						</DataRow>
					);
				});
			})}
		</tbody>
	);
};
